import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import {
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type ReactNode,
} from 'react';

import { cn } from '../../../utils/cn';
import { RoccoLink } from '../../../components/link/RoccoLink';
import { ReactComponent as InstagramMark } from '../../../icons/svg/brand/instagram.svg';
import { ReactComponent as FacebookMark } from '../../../icons/svg/brand/facebook.svg';
import { ReactComponent as LinkedInMark } from '../../../icons/svg/brand/linkedin.svg';
import { ReactComponent as YouTubeMark } from '../../../icons/svg/brand/youtube.svg';
import { ReactComponent as PinterestMark } from '../../../icons/svg/brand/pinterest.svg';
import type { HeaderNavLinks } from './Header';

interface MobileGlassMenuProps {
    primaryLinks: HeaderNavLinks;
    secondaryLinks: HeaderNavLinks;
    /** Drawn as a row of glyphs under the secondary links, keyed by title. */
    socialLinks?: HeaderNavLinks;
    isAuthorized: boolean;
    currentUrl: string;
    logout: () => void;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The header's menu glyph, in the header's own ink. */
    glyph: ReactNode;
}

/** The pill's size at rest: the glyph's own hit area. */
const PILL = 44;
/** The card's width open; its height is what the rows need. */
const CARD_WIDTH = 260;
/** The card's inset: the rows sit 8 in from its edge. */
const CARD_INSET = 8;

/**
 * What the card leaves between itself and the foot of the screen. The card
 * hangs from the header row, so without a cap a tall menu — a long font
 * scale, or a phone held in landscape — runs past the bottom of the screen
 * and `overflow-hidden` clips its own foot, taking Log in / Log out with it.
 */
const VIEWPORT_MARGIN = 24;

/** The spring the card grows on — the messenger sandbox's, to the number. */
const SPRING = 'cubic-bezier(0.3, 1.6, 0.5, 1)';

/**
 * A chevron pointing right, as the sandbox draws it: its down chevron turned
 * a quarter, in the muted ink.
 */
const RowChevron = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="size-4 -rotate-90 text-black/40"
        aria-hidden="true"
    >
        <path d="m6 9 6 6 6-6" />
    </svg>
);

/** The sign-out glyph: a door, and the arrow out of it. */
/**
 * The brand marks the article page's share row draws, filled from the
 * row's own colour: one set of marks across the site, not a second drawing
 * of each for the menu.
 */
const SOCIAL_GLYPHS: Record<string, typeof InstagramMark> = {
    Instagram: InstagramMark,
    Facebook: FacebookMark,
    LinkedIn: LinkedInMark,
    YouTube: YouTubeMark,
    Pinterest: PinterestMark,
};

const LogOutGlyph = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="size-[15px] shrink-0 text-black/55"
        aria-hidden="true"
    >
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <path d="M16 17l5-5-5-5" />
        <path d="M21 12H9" />
    </svg>
);

const ROW_CLASS =
    'flex w-full items-center justify-between rounded-lg px-2 py-4 text-left no-underline transition-colors hover:bg-black/5 hover:no-underline';
const DIVIDER_CLASS = 'mx-2 h-px bg-black/10';

/** What the focus trap cycles through inside the card. */
const FOCUSABLE = 'a[href], button:not([disabled])';

/**
 * The phone's navigation menu: the glyph's pill grows into a glass card
 * under the reader's thumb, with the site's ways in as rows.
 *
 * The messenger sandbox's Version D menu, to the pixel: the trigger is the
 * card — one element that springs from a 44px disc to a 260px card, its
 * glass (a displacement-mapped blur under a deeper blur, an inset highlight
 * over an inset shadow, a soft drop) settling in as it grows, the glyph
 * sliding out and the rows fading in a beat behind. At rest the disc is
 * invisible on the header: only the glyph shows, where it always has.
 *
 * Absolutely positioned over the glyph's place in the header row, so the
 * header's own layout never moves while the card is open.
 */
export const MobileGlassMenu = ({
    primaryLinks,
    secondaryLinks,
    socialLinks,
    isAuthorized,
    currentUrl,
    logout,
    open,
    onOpenChange,
    glyph,
}: MobileGlassMenuProps) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const rowsRef = useRef<HTMLDivElement>(null);
    const [cardHeight, setCardHeight] = useState(PILL);
    // How many rows there are, which is what changes the card's height — the
    // link objects themselves are new on every render.
    const rowCount =
        primaryLinks.links.length +
        secondaryLinks.links.length +
        (socialLinks?.links.length ?? 0);
    const [loggingOut, setLoggingOut] = useState(false);
    const encodedRedirect = encodeURIComponent(currentUrl);

    // The card's height is its rows', measured, so the spring has a number
    // to travel to; a CSS `auto` would not animate. Measured on the rows'
    // own block, not the centred panel around them: a centred overflow
    // runs off the top as much as the bottom, and a scrollHeight counts
    // only the bottom.
    useLayoutEffect(() => {
        if (!open) {
            setCardHeight(PILL);
            return;
        }
        const rows = rowsRef.current;
        if (!rows) return;
        const measure = () => {
            const wanted = rows.offsetHeight + CARD_INSET * 2;
            const room =
                window.innerHeight -
                (cardRef.current?.getBoundingClientRect().top ?? 0) -
                VIEWPORT_MARGIN;
            setCardHeight(Math.max(PILL, Math.min(wanted, room)));
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
        // Counted, not compared: the link objects these rows are built from are
        // rebuilt by the app on every render (`placeCountryDomain` returns a
        // fresh one), so naming them here never matched and the measure ran a
        // synchronous `offsetHeight` read on every render of the header —
        // including each one the scroll listener causes on a transparent hero.
    }, [open, rowCount, isAuthorized]);

    // Escape closes it. The tap-anywhere-else close is the backdrop's, not a
    // document listener's: a listener sees the tap but does not stop it, so
    // closing by tapping the page also opened whatever sat under the finger —
    // and under this card that is a grid of tiles, with almost no inert space
    // to aim at.
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onOpenChange(false);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onOpenChange]);

    // Focus goes into the card while it is open and comes back to the glyph
    // when it closes, so a keyboard or screen-reader reader is not left on a
    // hidden node — which browsers blur, dropping focus to the body.
    useEffect(() => {
        if (!open) return;
        const opener = triggerRef.current;
        rowsRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
        return () => opener?.focus();
    }, [open]);

    // The page does not scroll behind an open menu: the card is positioned in
    // the header row, so a scroll carries it off the screen with the bar.
    useEffect(() => {
        if (!open) return;
        const { body } = document;
        const previous = body.style.overflow;
        body.style.overflow = 'hidden';
        return () => {
            body.style.overflow = previous;
        };
    }, [open]);

    // Tab does not leave the card while it is open.
    const onCardKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Tab') return;
        const stops = Array.from(
            rowsRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
        );
        const first = stops[0];
        const last = stops[stops.length - 1];
        if (!first || !last) return;
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    return (
        <>
            {/* The glass's ripple: fractal noise displacing what shows through
                the card, so its edge bends the page a little as real glass
                would. Defined once, drawn nowhere. */}
            <svg aria-hidden="true" className="absolute size-0">
                <filter
                    id="liquid-glass"
                    x="0%"
                    y="0%"
                    width="100%"
                    height="100%"
                >
                    <feTurbulence
                        type="fractalNoise"
                        baseFrequency="0.012 0.012"
                        numOctaves="2"
                        seed="17"
                        result="noise"
                    />
                    <feDisplacementMap
                        in="SourceGraphic"
                        in2="noise"
                        scale="20"
                        xChannelSelector="R"
                        yChannelSelector="G"
                    />
                </filter>
            </svg>
            {/* Invisible, and only there to catch the tap that dismisses:
                without it the close also reached the page, which under this
                card is a grid of tile links. Under the card, over the bar. */}
            {open ? (
                <div
                    aria-hidden="true"
                    className="fixed inset-0 z-[120]"
                    onPointerDown={() => onOpenChange(false)}
                />
            ) : null}
            <div
                ref={cardRef}
                onKeyDown={onCardKeyDown}
                // Centred on the glyph: the row's 24 gutter puts the glyph's
                // 18 at x 24–42, mid 33, and the row's 76 puts it mid 38.
                className={cn(
                    // Over everything else on the bar — the wordmark rides at
                    // the fixed layer (z-fixed, 120), so the card sits one
                    // above it and still under the modals.
                    'absolute top-[16px] left-[11px] z-[121] overflow-hidden border',
                    // Closed radius is half the pill, not `rounded-full`: a
                    // 9999px radius tweens through pill shapes before landing
                    // on 16px, so the corners visibly morph while it grows.
                    open
                        ? 'rounded-[16px] border-white/60 bg-white/45 shadow-[inset_0_1px_1px_rgba(255,255,255,0.85),inset_0_-1px_1px_rgba(0,0,0,0.08),0_6px_24px_-6px_rgba(0,0,0,0.18)]'
                        : 'rounded-[22px] border-transparent bg-transparent shadow-none',
                )}
                style={{
                    width: open ? CARD_WIDTH : PILL,
                    height: cardHeight,
                    transition: `width 200ms ${SPRING}, height 200ms ${SPRING}, border-radius 200ms ease-out, background-color 200ms, border-color 200ms, box-shadow 200ms`,
                }}
            >
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 transition-opacity duration-[380ms]"
                    style={{
                        backdropFilter:
                            'url("#liquid-glass") blur(2px) saturate(170%)',
                        WebkitBackdropFilter: 'blur(2px) saturate(170%)',
                        borderRadius: 'inherit',
                        opacity: open ? 1 : 0,
                    }}
                />
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 transition-opacity duration-[380ms]"
                    style={{
                        backdropFilter: 'blur(20px) saturate(180%)',
                        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                        borderRadius: 'inherit',
                        opacity: open ? 1 : 0,
                    }}
                />
                {/* The glyph, where the header has always drawn it; it
                    slides out as the card grows. The button is the disc, and
                    the menu's name. */}
                <button
                    ref={triggerRef}
                    type="button"
                    aria-label="Toggle Navigation Menu"
                    aria-expanded={open}
                    // Over the card's own rows, so the glyph's place stays the
                    // glyph's. `pointer-events-none` here made the first row —
                    // which the card lays over this exact spot — take the tap,
                    // so reaching for the icon to close opened Products.
                    className={cn(
                        'relative z-[1] flex size-11 items-center justify-center border-0 bg-transparent p-0 outline-hidden transition-[opacity,transform] duration-200 focus-visible:outline-hidden',
                        open && '-translate-x-2 opacity-0',
                    )}
                    onClick={() => onOpenChange(!open)}
                >
                    {glyph}
                </button>
                {/* Fades in a beat behind the card; closed, it is also out
                    of the accessibility tree and the tab order (visibility
                    flips once the fade-out has run). */}
                <div
                    // At the card's open width from the start, so the rows
                    // never reflow while the card is still growing — and so
                    // their height, measured above, is the height they will
                    // have.
                    className={cn(
                        // `overscroll-contain` so reaching the end of a capped
                        // menu does not hand the scroll to the page behind it.
                        'absolute top-2 left-2 flex flex-col overflow-x-hidden overflow-y-auto overscroll-contain',
                        open ? 'visible opacity-100' : 'invisible opacity-0',
                    )}
                    style={{
                        width: CARD_WIDTH - CARD_INSET * 2,
                        maxHeight: cardHeight - CARD_INSET * 2,
                        transition: open
                            ? 'opacity 300ms 150ms, visibility 0s'
                            : 'opacity 300ms, visibility 0s 300ms',
                    }}
                >
                    <div ref={rowsRef}>
                        <nav>
                            {primaryLinks.links.map((item, index) => (
                                <div key={item.title}>
                                    {index > 0 ? (
                                        <div className={DIVIDER_CLASS} />
                                    ) : null}
                                    <RoccoLink
                                        className={ROW_CLASS}
                                        to={item.to}
                                        // On intent, not on sight. The rows
                                        // are mounted for the life of the
                                        // page — closed they are only
                                        // `invisible`, which an
                                        // IntersectionObserver does not care
                                        // about — so `viewport` fetched the
                                        // directory loaders on every mobile
                                        // page view whether or not the reader
                                        // ever opened the menu.
                                        prefetch="intent"
                                        isNonRemixRoute={item.isNonRemixRoute}
                                        tabIndex={open ? 0 : -1}
                                    >
                                        <span className="block text-[24px] leading-[1.15] font-normal text-black">
                                            {item.title}
                                        </span>
                                        <RowChevron />
                                    </RoccoLink>
                                </div>
                            ))}
                            {secondaryLinks.links.length > 0 ? (
                                <div className={DIVIDER_CLASS} />
                            ) : null}
                            {/* Centred in the room under the last rule: as
                                much above the rows as below them, before
                                the ways in and out at the foot. */}
                            <div className="py-[2.5rem]">
                                {secondaryLinks.links.map((item) => (
                                    <RoccoLink
                                        key={item.title}
                                        className={cn(ROW_CLASS, 'py-1.5')}
                                        to={item.to}
                                        isNonRemixRoute={item.isNonRemixRoute}
                                        tabIndex={open ? 0 : -1}
                                    >
                                        <span className="block text-[12px] text-black/55">
                                            {item.title}
                                        </span>
                                    </RoccoLink>
                                ))}
                                {socialLinks && socialLinks.links.length > 0 ? (
                                    // The marks in one row under the links,
                                    // each its own window onto the network.
                                    <div className="flex items-center gap-5 px-2 pt-4 text-black/35">
                                        {socialLinks.links.map((item) => {
                                            // A title with no drawing of its
                                            // own still gets its link, named
                                            // in words. Returning null here
                                            // meant a rename in the link set —
                                            // or a network nobody has drawn
                                            // yet — removed the link from the
                                            // menu with nothing said.
                                            const Glyph =
                                                SOCIAL_GLYPHS[item.title];
                                            return (
                                                <a
                                                    key={item.title}
                                                    href={item.to}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label={item.title}
                                                    tabIndex={open ? 0 : -1}
                                                    className="inline-flex text-black/35 transition-colors hover:text-black"
                                                >
                                                    {Glyph ? (
                                                        <Glyph
                                                            aria-hidden="true"
                                                            className="size-[18px] shrink-0 fill-current"
                                                        />
                                                    ) : (
                                                        <span className="text-[12px] leading-none">
                                                            {item.title}
                                                        </span>
                                                    )}
                                                </a>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </div>
                        </nav>
                        {isAuthorized ? (
                            // A row like the secondary links, with the door
                            // beside it, rather than a button.
                            <button
                                type="button"
                                // Released if the logout throws. Latched on
                                // the way in with no way out, a logout that
                                // failed left the menu's only sign-out control
                                // disabled until the page was reloaded. A
                                // logout that succeeds navigates away and
                                // takes this button with it, so there is
                                // nothing to release on that path.
                                onClick={() => {
                                    setLoggingOut(true);
                                    try {
                                        logout();
                                    } catch (error) {
                                        setLoggingOut(false);
                                        throw error;
                                    }
                                }}
                                disabled={loggingOut}
                                tabIndex={open ? 0 : -1}
                                className={cn(
                                    ROW_CLASS,
                                    'justify-end gap-2 py-1.5 disabled:opacity-60',
                                )}
                            >
                                <LogOutGlyph />
                                <span className="block text-[12px] text-black/55">
                                    Log out
                                </span>
                            </button>
                        ) : (
                            <div className="flex gap-2 px-2 pb-1">
                                <RoccoLink
                                    className="flex-1 rounded-lg border border-black/15 py-2 text-center text-[13px] font-medium text-black no-underline hover:no-underline"
                                    to={`/login?redirect=${encodedRedirect}`}
                                    tabIndex={open ? 0 : -1}
                                >
                                    Log in
                                </RoccoLink>
                                <RoccoLink
                                    className="flex-1 rounded-lg bg-black py-2 text-center text-[13px] font-medium text-white no-underline hover:no-underline"
                                    to={`/login?redirect=${encodedRedirect}`}
                                    tabIndex={open ? 0 : -1}
                                >
                                    Sign up
                                </RoccoLink>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
